import React, { useState, useEffect, useCallback } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import DashboardContainer from '../components/layout/DashboardContainer';
import FlowCanvas, { syncPositions } from '../components/bot/FlowCanvas';
import NodeEditor from '../components/bot/NodeEditor';
import FlowTestModal from '../components/bot/FlowTestModal';
import {
    fetchBotFlows,
    createBotFlow,
    updateBotFlow,
    activateBotFlow,
    deactivateBot,
    deleteBotFlow,
    fetchBotSettings,
} from '../services/api';
import './BotFlowPage.css';

export default function BotFlowPage() {
    const [flows, setFlows] = useState([]);
    const [currentFlow, setCurrentFlow] = useState(null);
    const [currentFlowId, setCurrentFlowId] = useState(null);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [isTestOpen, setIsTestOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [botSettings, setBotSettings] = useState(null);
    const [toast, setToast] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [showGlobalSettings, setShowGlobalSettings] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const loadFlows = useCallback(async () => {
        setIsLoading(true);
        try {
            const [flowsRes, settingsRes] = await Promise.all([
                fetchBotFlows(),
                fetchBotSettings(),
            ]);
            setFlows(flowsRes.data);
            setBotSettings(settingsRes.data);

            // Do not auto-select a flow; let the user see the list view first.
        } catch (err) {
            console.error('Failed to load bot flows:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadFlows(); }, [loadFlows]);

    const buildFlow = (f) => ({
        name: f.name,
        root_node_id: f.root_node_id,
        nodes: f.nodes,
        global_keywords: f.global_keywords || [],
        fallback_message: f.fallback_message || "Sorry, I didn't understand. Please choose one of the options above.",
        session_timeout_minutes: f.session_timeout_minutes || 30,
    });

    /* ─── Flow actions ──── */
    const handleCreateNew = () => {
        const welcomeNode = {
            node_id: 'welcome',
            type: 'menu',
            label: 'Welcome',
            message: '',
            presentation: 'buttons',
            header_text: '',
            footer_text: '',
            action_title: 'View options',
            section_title: 'Options',
            options: [],
            fallback_node: null,
            notify_owner: false,
            position: { x: 300, y: 50 },
        };
        setCurrentFlow({
            name: 'New Flow',
            root_node_id: 'welcome',
            nodes: [welcomeNode],
            global_keywords: [],
            fallback_message: "Sorry, I didn't understand. Please choose one of the options above.",
            session_timeout_minutes: 30,
        });
        setCurrentFlowId(null);
        setSelectedNodeId('welcome');
        setDirty(true);
    };

    const handleSave = async () => {
        if (!currentFlow) return;
        if (!currentFlow.name?.trim()) { showToast('Enter a flow name.', 'error'); return; }
        if (currentFlow.nodes.length === 0) { showToast('Add at least one node.', 'error'); return; }
        setIsSaving(true);
        try {
            if (currentFlowId) {
                await updateBotFlow(currentFlowId, currentFlow);
                showToast('Flow saved!');
            } else {
                const res = await createBotFlow(currentFlow);
                setCurrentFlowId(res.data.id);
                showToast('Flow created!');
            }
            setDirty(false);
            loadFlows();
        } catch (err) {
            showToast(err.response?.data?.detail || 'Save failed', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleActivate = async () => {
        if (!currentFlowId) { showToast('Save the flow first.', 'error'); return; }
        try { await activateBotFlow(currentFlowId); showToast('Bot activated!'); loadFlows(); }
        catch { showToast('Failed to activate.', 'error'); }
    };

    const handleDeactivate = async () => {
        try { await deactivateBot(); showToast('Bot deactivated.'); loadFlows(); }
        catch { showToast('Failed to deactivate.', 'error'); }
    };

    const handleDeleteFlow = async (flowId) => {
        if (!window.confirm('Delete this flow?')) return;
        try {
            await deleteBotFlow(flowId);
            showToast('Flow deleted.');
            if (currentFlowId === flowId) {
                setCurrentFlow(null); setCurrentFlowId(null); setSelectedNodeId(null);
            }
            loadFlows();
        } catch { showToast('Failed to delete.', 'error'); }
    };

    const handleSelectFlow = (flow) => {
        setCurrentFlow(buildFlow(flow));
        setCurrentFlowId(flow.id);
        setSelectedNodeId(null);
        setDirty(false);
    };

    /* ─── Node actions ──── */
    const updateFlow = (updates) => {
        setCurrentFlow(prev => ({ ...prev, ...updates }));
        setDirty(true);
    };

    const addNode = () => {
        const id = `node_${Date.now()}`;
        const newNode = {
            node_id: id,
            type: 'menu',
            label: 'New Node',
            message: '',
            presentation: 'buttons',
            header_text: '',
            footer_text: '',
            action_title: 'View options',
            section_title: 'Options',
            options: [],
            fallback_node: null,
            notify_owner: false,
            position: { x: 150 + Math.random() * 300, y: 100 + Math.random() * 200 },
        };
        updateFlow({ nodes: [...(currentFlow?.nodes || []), newNode] });
        setSelectedNodeId(id);
    };

    const updateNode = (nodeId, updates) => {
        updateFlow({
            nodes: currentFlow.nodes.map(n =>
                n.node_id === nodeId ? { ...n, ...updates } : n
            ),
        });
    };

    const deleteNode = (nodeId) => {
        if (currentFlow.root_node_id === nodeId) { showToast('Cannot delete the entry node!', 'error'); return; }
        updateFlow({ nodes: currentFlow.nodes.filter(n => n.node_id !== nodeId) });
        setSelectedNodeId(null);
    };

    const handleNodesChange = useCallback((posChanges) => {
        setCurrentFlow(prev => {
            if (!prev) return prev;
            const updated = { ...prev, nodes: [...prev.nodes] };
            posChanges.forEach(change => {
                const idx = updated.nodes.findIndex(n => n.node_id === change.id);
                if (idx !== -1 && change.position) {
                    updated.nodes[idx] = { ...updated.nodes[idx], position: change.position };
                }
            });
            return updated;
        });
        setDirty(true);
    }, []);

    /* ─── Global keywords ──── */
    const addKeyword = () => {
        updateFlow({
            global_keywords: [
                ...(currentFlow?.global_keywords || []),
                { keyword: '', node_id: currentFlow?.root_node_id || '' },
            ],
        });
    };

    const updateKeyword = (i, field, value) => {
        const kws = [...(currentFlow?.global_keywords || [])];
        kws[i] = { ...kws[i], [field]: value };
        updateFlow({ global_keywords: kws });
    };

    const removeKeyword = (i) => {
        updateFlow({ global_keywords: (currentFlow?.global_keywords || []).filter((_, idx) => idx !== i) });
    };

    const selectedNode = currentFlow?.nodes?.find(n => n.node_id === selectedNodeId);
    const isActive = botSettings?.bot_active && botSettings?.active_flow_id === currentFlowId;

    /* ─── RENDER ──── */
    if (isLoading) {
        return (
            <DashboardContainer>
                <div className="bot-flow-page"><div className="bot-loading">Loading bot builder...</div></div>
            </DashboardContainer>
        );
    }

    return (
        <DashboardContainer>
            <div className="bot-flow-page">
                {toast && <div className={`bot-toast ${toast.type}`}>{toast.msg}</div>}

                {/* Toolbar */}
                <div className="bot-toolbar">
                    <div className="bot-toolbar-left">
                        {currentFlow ? (
                            <button className="tb-btn secondary" onClick={() => { setCurrentFlow(null); setCurrentFlowId(null); }}>
                                &larr; Back to Flows
                            </button>
                        ) : (
                            <h1>Bot Flows</h1>
                        )}
                        {currentFlow && (
                            <>
                                <input
                                    className="flow-name-input"
                                    type="text"
                                    value={currentFlow.name}
                                    onChange={e => updateFlow({ name: e.target.value })}
                                    placeholder="Flow name..."
                                />
                                <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                                    {isActive ? 'Active' : 'Inactive'}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="bot-toolbar-right">
                        {currentFlow && (
                            <>
                                <button className="tb-btn secondary" onClick={() => setShowGlobalSettings(!showGlobalSettings)}>
                                    Settings
                                </button>
                                <button className="tb-btn secondary" onClick={() => setIsTestOpen(true)}>
                                    Test
                                </button>
                                <button className="tb-btn primary" onClick={handleSave} disabled={isSaving || !dirty}>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </button>
                                {currentFlowId && !isActive && (
                                    <button className="tb-btn success" onClick={handleActivate}>Activate</button>
                                )}
                                {isActive && (
                                    <button className="tb-btn danger" onClick={handleDeactivate}>Deactivate</button>
                                )}
                                {currentFlowId && (
                                    <button className="tb-btn danger-outline" onClick={() => handleDeleteFlow(currentFlowId)}>Delete</button>
                                )}
                            </>
                        )}
                        {!currentFlow && flows.length > 0 && (
                            <button className="tb-btn primary" onClick={handleCreateNew}>+ New Flow</button>
                        )}
                    </div>
                </div>

                {/* List View / Empty State */}
                {!currentFlow && (
                    <div className="bot-list-view">
                        {flows.length === 0 ? (
                            <div className="bot-empty">
                                <h2>Build your first bot flow</h2>
                                <p>Create a visual decision tree that auto-responds to customers natively on WhatsApp.</p>
                                <button className="tb-btn primary large" onClick={handleCreateNew}>Get Started</button>
                            </div>
                        ) : (
                            <div className="bot-cards-grid">
                                {flows.map(f => (
                                    <div key={f.id} className={`bot-card ${f.is_active ? 'active' : ''}`} onClick={() => handleSelectFlow(f)}>
                                        <div className="bot-card-header">
                                            <h3>{f.name}</h3>
                                            {f.is_active ? (
                                                <span className="status-badge active">Active</span>
                                            ) : (
                                                <span className="status-badge inactive">Inactive</span>
                                            )}
                                        </div>
                                        <div className="bot-card-body">
                                            <div className="bc-stat">
                                                <span className="bc-value">{f.nodes?.length || 0}</span>
                                                <span className="bc-label">Nodes</span>
                                            </div>
                                            <div className="bc-stat">
                                                <span className="bc-value">{f.global_keywords?.length || 0}</span>
                                                <span className="bc-label">Keywords</span>
                                            </div>
                                        </div>
                                        <div className="bot-card-footer">
                                            <span className="bc-action">Edit Flow &rarr;</span>
                                            <button
                                                className="bc-delete"
                                                onClick={(e) => { e.stopPropagation(); handleDeleteFlow(f.id); }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Main area: Canvas + Node Editor */}
                {currentFlow && (
                    <div className={`bot-main ${selectedNode ? 'with-editor' : ''}`}>
                        <ReactFlowProvider>
                            <FlowCanvas
                                flow={currentFlow}
                                selectedNodeId={selectedNodeId}
                                onNodeSelect={setSelectedNodeId}
                                onNodesChange={handleNodesChange}
                                onAddNode={addNode}
                            />
                        </ReactFlowProvider>

                        {selectedNode && (
                            <NodeEditor
                                node={selectedNode}
                                allNodes={currentFlow.nodes}
                                onUpdate={updateNode}
                                onDelete={deleteNode}
                                onClose={() => setSelectedNodeId(null)}
                                isRoot={currentFlow.root_node_id === selectedNode.node_id}
                            />
                        )}
                    </div>
                )}

                {/* Global Settings drawer */}
                {showGlobalSettings && currentFlow && (
                    <div className="global-drawer">
                        <div className="gd-header">
                            <h3>Global Settings</h3>
                            <button className="ne-close" onClick={() => setShowGlobalSettings(false)}>x</button>
                        </div>
                        <div className="gd-body">
                            <div className="ne-field">
                                <label>Entry Node</label>
                                <select
                                    value={currentFlow.root_node_id}
                                    onChange={e => updateFlow({ root_node_id: e.target.value })}
                                >
                                    {currentFlow.nodes.map(n => (
                                        <option key={n.node_id} value={n.node_id}>{n.label || n.node_id}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="ne-field">
                                <label>Session Timeout (minutes)</label>
                                <input
                                    type="number"
                                    value={currentFlow.session_timeout_minutes}
                                    onChange={e => updateFlow({ session_timeout_minutes: parseInt(e.target.value) || 30 })}
                                    min={1} max={1440}
                                />
                            </div>
                            <div className="ne-field">
                                <label>Fallback Message</label>
                                <input
                                    type="text"
                                    value={currentFlow.fallback_message}
                                    onChange={e => updateFlow({ fallback_message: e.target.value })}
                                />
                            </div>
                            <div className="ne-field">
                                <label>Global Keywords</label>
                                <div className="gd-keywords">
                                    {(currentFlow.global_keywords || []).map((kw, i) => (
                                        <div key={i} className="gd-kw-row">
                                            <input
                                                type="text"
                                                value={kw.keyword}
                                                onChange={e => updateKeyword(i, 'keyword', e.target.value)}
                                                placeholder='e.g. "menu"'
                                            />
                                            <span className="gd-arrow">goes to</span>
                                            <select
                                                value={kw.node_id}
                                                onChange={e => updateKeyword(i, 'node_id', e.target.value)}
                                            >
                                                <option value="">-- Node --</option>
                                                {currentFlow.nodes.map(n => (
                                                    <option key={n.node_id} value={n.node_id}>{n.label || n.node_id}</option>
                                                ))}
                                            </select>
                                            <button className="ne-opt-remove" onClick={() => removeKeyword(i)}>x</button>
                                        </div>
                                    ))}
                                    <button className="ne-add-option" onClick={addKeyword}>+ Add Keyword</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Test modal */}
                {isTestOpen && currentFlow && (
                    <FlowTestModal flow={currentFlow} onClose={() => setIsTestOpen(false)} />
                )}
            </div>
        </DashboardContainer>
    );
}
